import React, { useState, useEffect } from 'react'
import { PageHeader } from '@/components/layout'
import { MembershipBadge } from '@/components/membership/MembershipBadge'
import { useAdminMemberships, useOverrideTier, useRecalculateMemberships, useMembershipThresholds, useUpdateMembershipThresholds } from '@/hooks/useMembership'
import type { MembershipTier, MembershipOverrideRequest } from '@/types/membership'

export const AdminMembershipsPage: React.FC = () => {
  const [search, setSearch] = useState('')
  const [tierFilter, setTierFilter] = useState('')
  const [page, setPage] = useState(1)
  const [overrideModal, setOverrideModal] = useState<{ userId: string; userName: string; currentTier: MembershipTier } | null>(null)
  const [overrideData, setOverrideData] = useState<MembershipOverrideRequest>({ tier: 'bronze', reason: '' })
  const [showThresholds, setShowThresholds] = useState(false)
  const [thresholdValues, setThresholdValues] = useState({
    bronze: 1, bronze_period: 'alltime' as 'alltime' | 'yearly',
    silver: 5, silver_period: 'alltime' as 'alltime' | 'yearly',
    gold: 10, gold_period: 'alltime' as 'alltime' | 'yearly',
    platinum: 10, platinum_period: 'yearly' as 'alltime' | 'yearly',
    vip: 20, vip_period: 'alltime' as 'alltime' | 'yearly',
  })
  const [thresholdSaved, setThresholdSaved] = useState(false)

  const { data, isLoading, error } = useAdminMemberships({
    search: search || undefined,
    tier: tierFilter || undefined,
    page,
    limit: 20,
  })

  const overrideMutation = useOverrideTier()
  const recalculateMutation = useRecalculateMemberships()
  const { data: thresholds } = useMembershipThresholds()
  const updateThresholdsMutation = useUpdateMembershipThresholds()

  useEffect(() => {
    if (thresholds) {
      setThresholdValues({
        bronze: thresholds.membership_bronze_threshold,
        bronze_period: thresholds.membership_bronze_period,
        silver: thresholds.membership_silver_threshold,
        silver_period: thresholds.membership_silver_period,
        gold: thresholds.membership_gold_threshold,
        gold_period: thresholds.membership_gold_period,
        platinum: thresholds.membership_platinum_threshold,
        platinum_period: thresholds.membership_platinum_period,
        vip: thresholds.membership_vip_threshold,
        vip_period: thresholds.membership_vip_period,
      })
    }
  }, [thresholds])

  const memberships = data?.data || []
  const meta = data?.meta

  const handleOverride = async () => {
    if (!overrideModal) return
    await overrideMutation.mutateAsync({
      userId: overrideModal.userId,
      request: overrideData,
    })
    setOverrideModal(null)
    setOverrideData({ tier: 'bronze', reason: '' })
  }

  const handleRecalculate = async () => {
    await recalculateMutation.mutateAsync()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-500">Gagal memuat data membership</p>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Membership"
        subtitle="Kelola membership peserta"
      />

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="relative flex-1 min-w-0 md:max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-500 text-[20px]">
            search
          </span>
          <input
            type="text"
            placeholder="Cari peserta..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50"
          />
        </div>
        <select
          value={tierFilter}
          onChange={(e) => { setTierFilter(e.target.value); setPage(1) }}
          className="px-4 py-2.5 bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:border-primary/50"
        >
          <option value="">Semua Tier</option>
          <option value="none">Peserta</option>
          <option value="bronze">Bronze</option>
          <option value="silver">Silver</option>
          <option value="gold">Gold</option>
          <option value="platinum">Platinum</option>
          <option value="vip">VIP</option>
        </select>
        <button
          onClick={handleRecalculate}
          disabled={recalculateMutation.isPending}
          className="px-4 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-[18px]">refresh</span>
          {recalculateMutation.isPending ? 'Menghitung...' : 'Recalculate All'}
        </button>
        <button
          onClick={() => setShowThresholds(!showThresholds)}
          className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${showThresholds ? 'bg-primary text-white' : 'bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-50'}`}
        >
          <span className="material-symbols-outlined text-[18px]">tune</span>
          Threshold
        </button>
      </div>

      {showThresholds && (
        <div className="bg-white dark:bg-surface-card rounded-lg border border-slate-200 dark:border-white/10 p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Pengaturan Threshold Membership</h3>
              <p className="text-xs text-slate-500 mt-1">Atur jumlah kehadiran hybrid yang diperlukan untuk mencapai setiap tier</p>
            </div>
            {thresholdSaved && (
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">Tersimpan!</span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1.5 block">Bronze</label>
              <div className="flex items-center gap-2">
                <MembershipBadge tier="bronze" size="sm" />
                <input
                  type="number"
                  min={1}
                  value={thresholdValues.bronze}
                  onChange={(e) => setThresholdValues({ ...thresholdValues, bronze: Number(e.target.value) })}
                  className="w-20 px-3 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:border-primary/50"
                />
              </div>
              <select
                value={thresholdValues.bronze_period}
                onChange={(e) => setThresholdValues({ ...thresholdValues, bronze_period: e.target.value as 'alltime' | 'yearly' })}
                className="mt-1.5 w-full px-2 py-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded text-[10px] font-bold text-slate-600 dark:text-slate-400 focus:outline-none"
              >
                <option value="alltime">Semua Waktu</option>
                <option value="yearly">Per Tahun</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1.5 block">Silver</label>
              <div className="flex items-center gap-2">
                <MembershipBadge tier="silver" size="sm" />
                <input
                  type="number"
                  min={1}
                  value={thresholdValues.silver}
                  onChange={(e) => setThresholdValues({ ...thresholdValues, silver: Number(e.target.value) })}
                  className="w-20 px-3 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:border-primary/50"
                />
              </div>
              <select
                value={thresholdValues.silver_period}
                onChange={(e) => setThresholdValues({ ...thresholdValues, silver_period: e.target.value as 'alltime' | 'yearly' })}
                className="mt-1.5 w-full px-2 py-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded text-[10px] font-bold text-slate-600 dark:text-slate-400 focus:outline-none"
              >
                <option value="alltime">Semua Waktu</option>
                <option value="yearly">Per Tahun</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1.5 block">Gold</label>
              <div className="flex items-center gap-2">
                <MembershipBadge tier="gold" size="sm" />
                <input
                  type="number"
                  min={1}
                  value={thresholdValues.gold}
                  onChange={(e) => setThresholdValues({ ...thresholdValues, gold: Number(e.target.value) })}
                  className="w-20 px-3 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:border-primary/50"
                />
              </div>
              <select
                value={thresholdValues.gold_period}
                onChange={(e) => setThresholdValues({ ...thresholdValues, gold_period: e.target.value as 'alltime' | 'yearly' })}
                className="mt-1.5 w-full px-2 py-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded text-[10px] font-bold text-slate-600 dark:text-slate-400 focus:outline-none"
              >
                <option value="alltime">Semua Waktu</option>
                <option value="yearly">Per Tahun</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1.5 block">Platinum</label>
              <div className="flex items-center gap-2">
                <MembershipBadge tier="platinum" size="sm" />
                <input
                  type="number"
                  min={1}
                  value={thresholdValues.platinum}
                  onChange={(e) => setThresholdValues({ ...thresholdValues, platinum: Number(e.target.value) })}
                  className="w-20 px-3 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:border-primary/50"
                />
              </div>
              <select
                value={thresholdValues.platinum_period}
                onChange={(e) => setThresholdValues({ ...thresholdValues, platinum_period: e.target.value as 'alltime' | 'yearly' })}
                className="mt-1.5 w-full px-2 py-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded text-[10px] font-bold text-slate-600 dark:text-slate-400 focus:outline-none"
              >
                <option value="alltime">Semua Waktu</option>
                <option value="yearly">Per Tahun</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-1.5 block">VIP</label>
              <div className="flex items-center gap-2">
                <MembershipBadge tier="vip" size="sm" />
                <input
                  type="number"
                  min={1}
                  value={thresholdValues.vip}
                  onChange={(e) => setThresholdValues({ ...thresholdValues, vip: Number(e.target.value) })}
                  className="w-20 px-3 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:border-primary/50"
                />
              </div>
              <select
                value={thresholdValues.vip_period}
                onChange={(e) => setThresholdValues({ ...thresholdValues, vip_period: e.target.value as 'alltime' | 'yearly' })}
                className="mt-1.5 w-full px-2 py-1 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded text-[10px] font-bold text-slate-600 dark:text-slate-400 focus:outline-none"
              >
                <option value="alltime">Semua Waktu</option>
                <option value="yearly">Per Tahun</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={async () => {
                await updateThresholdsMutation.mutateAsync(thresholdValues)
                setThresholdSaved(true)
                setTimeout(() => setThresholdSaved(false), 2000)
              }}
              disabled={updateThresholdsMutation.isPending}
              className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:opacity-90 transition-all disabled:opacity-50"
            >
              {updateThresholdsMutation.isPending ? 'Menyimpan...' : 'Simpan Threshold'}
            </button>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-white dark:bg-surface-card border border-slate-100 dark:border-white/5 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-white/[0.01] border-b border-slate-100 dark:border-white/5">
                <th className="text-left px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400">PESERTA</th>
                <th className="text-left px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400">TIER</th>
                <th className="text-left px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400 hidden md:table-cell">TOTAL HADIR</th>
                <th className="text-left px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400 hidden md:table-cell">TAHUN INI</th>
                <th className="text-left px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400 hidden lg:table-cell">DIPEROLEH</th>
                <th className="text-right px-8 py-4 text-xs font-black tracking-[0.2em] text-slate-400">AKSI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/5">
              {memberships.map((m) => (
                <tr key={m.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-full bg-gradient-to-br from-primary/40 to-primary/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-red-300">{(m.userName || m.userId).charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{m.userName || 'Unknown'}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{m.userEmail || ''}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <MembershipBadge tier={m.tier} size="sm" />
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">
                    {m.totalAttendance}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">
                    {m.yearAttendance}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden lg:table-cell">
                    {m.earnedAt ? new Date(m.earnedAt).toLocaleDateString('id-ID') : '-'}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setOverrideModal({ userId: m.userId, userName: m.userName || m.userId, currentTier: m.tier })}
                        className="size-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center hover:bg-purple-500/20 transition-colors"
                        title="Override Tier"
                      >
                        <span className="material-symbols-outlined text-[16px]">admin_panel_settings</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {meta && meta.totalPages && meta.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg text-sm font-bold bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
          >
            Sebelumnya
          </button>
          <span className="px-4 py-2 text-sm text-slate-500">
            Halaman {page} dari {meta.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(meta.totalPages!, p + 1))}
            disabled={page === meta.totalPages}
            className="px-4 py-2 rounded-lg text-sm font-bold bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
          >
            Selanjutnya
          </button>
        </div>
      )}

      {overrideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setOverrideModal(null)} />
          <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Override Tier</h3>
            <p className="text-sm text-gray-500 dark:text-text-muted mb-6">
              Ubah tier <span className="font-medium text-gray-900 dark:text-white">{overrideModal.userName}</span> dari{' '}
              <MembershipBadge tier={overrideModal.currentTier} size="sm" showLabel /> ke:
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 block">Tier Baru</label>
                <select
                  value={overrideData.tier}
                  onChange={(e) => setOverrideData({ ...overrideData, tier: e.target.value as MembershipTier })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-lg text-sm font-medium focus:border-primary/50 outline-none"
                >
                  <option value="none">Peserta</option>
                  <option value="bronze">Bronze</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                  <option value="vip">VIP</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 block">Alasan</label>
                <textarea
                  value={overrideData.reason}
                  onChange={(e) => setOverrideData({ ...overrideData, reason: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-lg text-sm font-medium focus:border-primary/50 outline-none resize-none"
                  rows={3}
                  placeholder="Alasan override tier..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setOverrideModal(null)}
                className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleOverride}
                disabled={overrideMutation.isPending || !overrideData.reason}
                className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg transition-colors disabled:opacity-50 font-bold text-sm"
              >
                {overrideMutation.isPending ? 'Memproses...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminMembershipsPage
